/**
 * TMS-2 through TMS-6 seed data:
 *   • carrier_invoices + rate_confirmations + disputes  (TMS-2)
 *   • consolidation_opportunities                       (TMS-3)
 *   • emissions_factors + emissions_reports             (TMS-4)
 *   • scenarios                                          (TMS-5)
 *   • customs_documents + multimodal loads              (TMS-6)
 */
import { DEMO_TENANT_ID } from './tenantDefaults';

const TENANT = DEMO_TENANT_ID;
const iso = (days = 0, hours = 0) => new Date(Date.now() - (days * 86400000 + hours * 3600000)).toISOString();
const dim = (dimension, risk, severity) => ({ dimension, risk, severity });

// ── TMS-2 Freight Audit ───────────────────────────────────────────────
export const SEED_RATE_CONFIRMATIONS = [
  { id: 'rc-0001', tenantId: TENANT, loadId: 'load-00001', carrierId: 'carrier-od',  agreedRate: 2850, terms: 'NET30', signedAt: iso(14) },
  { id: 'rc-0002', tenantId: TENANT, loadId: 'load-00002', carrierId: 'carrier-xpo', agreedRate: 1420, terms: 'NET30', signedAt: iso(10) },
  { id: 'rc-0003', tenantId: TENANT, loadId: 'load-00003', carrierId: 'carrier-estes', agreedRate: 2150, terms: 'NET45', signedAt: iso(8) },
  { id: 'rc-0004', tenantId: TENANT, loadId: 'load-00004', carrierId: 'carrier-saia', agreedRate: 1780, terms: 'NET30', signedAt: iso(6) },
  { id: 'rc-0005', tenantId: TENANT, loadId: 'load-00005', carrierId: 'carrier-abf',  agreedRate: 3200, terms: 'NET30', signedAt: iso(4) },
];

export const SEED_CARRIER_INVOICES = [
  // Match — auto-approve
  { id: 'inv-0001', tenantId: TENANT, invoiceNumber: 'OD-44821', carrierId: 'carrier-od',   loadId: 'load-00001', totalAmount: 2850, lineItems: [{ desc: 'Linehaul', amount: 2850 }], status: 'approved', receivedAt: iso(10), factored: false },
  // 3% variance — within tolerance
  { id: 'inv-0002', tenantId: TENANT, invoiceNumber: 'XPO-8741', carrierId: 'carrier-xpo',  loadId: 'load-00002', totalAmount: 1462, lineItems: [{ desc: 'Linehaul', amount: 1420 }, { desc: 'Detention', amount: 42 }], status: 'approved', receivedAt: iso(8), factored: false },
  // 12% variance — dispute
  { id: 'inv-0003', tenantId: TENANT, invoiceNumber: 'EXL-3019', carrierId: 'carrier-estes',loadId: 'load-00003', totalAmount: 2410, lineItems: [{ desc: 'Linehaul', amount: 2150 }, { desc: 'Layover', amount: 260 }], status: 'disputed', receivedAt: iso(5), factored: false, matchResult: { pctVariance: 12.1, recommendation: 'Variance 12% exceeds tolerance. Open dispute.' } },
  // Aged 45 days
  { id: 'inv-0004', tenantId: TENANT, invoiceNumber: 'SAIA-211', carrierId: 'carrier-saia', loadId: 'load-00004', totalAmount: 1780, status: 'approved', receivedAt: iso(45), factored: false },
  // Aged 75 days
  { id: 'inv-0005', tenantId: TENANT, invoiceNumber: 'ABFS-612', carrierId: 'carrier-abf',  loadId: 'load-00005', totalAmount: 3200, status: 'approved', receivedAt: iso(75), factored: true, factorId: 'TBS-88221' },
  // Paid
  { id: 'inv-0006', tenantId: TENANT, invoiceNumber: 'ODFL-4492', carrierId: 'carrier-od',  loadId: 'load-00006', totalAmount: 2650, status: 'paid', receivedAt: iso(35), paidAt: iso(15), factored: false },
];

export const SEED_DISPUTES = [
  { id: 'dsp-0001', tenantId: TENANT, invoiceId: 'inv-0003', carrierId: 'carrier-estes', loadId: 'load-00003', variance: 12.1, reason: 'auto_opened_three_way_mismatch', note: 'Layover charge not on rate con', openedBy: 'aegis', status: 'open', openedAt: iso(5) },
];

// ── TMS-3 Consolidation Opportunities ─────────────────────────────────
export const SEED_CONSOLIDATION_OPPORTUNITIES = [
  // ── copp-0001: IL::IN — same-lane consolidation ─────────────────────────
  {
    id: 'copp-0001', tenantId: TENANT, type: 'consolidation',
    loadIds: ['load-00011', 'load-00012'], lane: 'IL::IN',
    weightCombined: 38000, pickupGapHours: 4, estSavings: 312,
    score: 88, status: 'open',
    reason: 'Two loads on IL→IN within 4h window — combined weight 38,000 lbs.',
    identifiedAt: iso(0, 2),
    challenges: [
      dim('service_risk', 'High-frequency IL→IN lane with multiple LTL carrier options; capacity confirmed.', 'none'),
      dim('appointment_window_conflict', '4h pickup gap is comfortably within co-load window for Chicago-area facilities.', 'none'),
      dim('accessorial_blowback', 'Both loads are standard dry van pallets — no conflicting accessorial requirements.', 'none'),
      dim('weight_class_escalation', '38,000 lbs combined stays within NMFC Class 70 for general freight.', 'low'),
      dim('customer_service_conflict', 'No shared customer; independent delivery windows — no SLA conflict.', 'none'),
    ],
    overallRisk: 'low', proceedRecommendation: true,
    confidence: 0.88,
    confidenceFactors: ['lane_history', 'weight_utilization', 'pickup_gap_optimal'],
    narrative: 'Two loads on IL→IN within a 4h pickup window at 38,000 lbs combined — $312 savings with carrier capacity confirmed. Low risk across all dimensions on a high-frequency lane.',
    challengeSummary: 'Clean consolidation on a familiar lane. No risk vectors.',
  },
  // ── copp-0002: TX → CA — long-haul backhaul, moderate risk ──────────────
  {
    id: 'copp-0002', tenantId: TENANT, type: 'backhaul',
    loadIds: ['load-00008', 'load-00020'], lane: 'TX → CA',
    returningAssetId: 'carrier-od', estSavings: 685,
    score: 76, status: 'open',
    reason: 'Asset returning from TX — matches origin of load L-10020.',
    identifiedAt: iso(0, 4),
    challenges: [
      dim('service_risk', 'TX→CA backhaul is a 1,400-mile deviation — OD driver may prefer deadhead; confirm willingness before committing.', 'medium'),
      dim('appointment_window_conflict', 'Destination delivery window at CA receiver needs confirmation; long-haul adds transit variability.', 'low'),
      dim('accessorial_blowback', 'Long-haul backhaul may trigger fuel surcharge renegotiation — est. $45–80 impact on net savings.', 'low'),
      dim('weight_class_escalation', 'No weight class change — load is within FTL Class 70 limits.', 'none'),
      dim('customer_service_conflict', 'Single receiver on TX→CA leg — no inter-customer conflicts.', 'none'),
    ],
    overallRisk: 'medium', proceedRecommendation: true,
    confidence: 0.76,
    confidenceFactors: ['asset_returning_confirmed', 'long_haul_risk_adjusted', 'savings_vs_surcharge_net_positive'],
    narrative: 'TX→CA backhaul with returning OD asset offers $685 in gross savings, net ~$605 after potential fuel surcharge. Moderate risk: 1,400-mile deviation requires driver commitment. Confirm OD availability and CA delivery window before ratifying.',
    challengeSummary: 'Good savings but confirm carrier commitment — long-haul backhaul deviation has 76% acceptance on this lane.',
  },
  // ── copp-0003: GA::FL — partial loads, low risk ──────────────────────────
  {
    id: 'copp-0003', tenantId: TENANT, type: 'consolidation',
    loadIds: ['load-00015', 'load-00017'], lane: 'GA::FL',
    weightCombined: 32000, pickupGapHours: 8, estSavings: 248,
    score: 72, status: 'open',
    reason: 'Two partial loads on GA→FL within 8h pickup window.',
    identifiedAt: iso(0, 8),
    challenges: [
      dim('service_risk', 'GA→FL is a well-established LTL-to-FTL upgrade lane; capacity available.', 'none'),
      dim('appointment_window_conflict', '8h gap is manageable for co-loading with a midday pickup coordination.', 'low'),
      dim('accessorial_blowback', 'Standard dry freight — no accessorial conflicts.', 'none'),
      dim('weight_class_escalation', '32,000 lbs combined stays well within FTL weight thresholds.', 'none'),
      dim('customer_service_conflict', 'No shared customers — independent delivery windows confirmed.', 'none'),
    ],
    overallRisk: 'low', proceedRecommendation: true,
    confidence: 0.72,
    confidenceFactors: ['standard_consolidation', 'lane_history', 'pickup_gap_manageable'],
    narrative: 'Two partial loads on GA→FL within an 8h pickup window — $248 savings. Lower priority vs IL::TX and CA::NV but clean execution with no material risk.',
    challengeSummary: 'Straightforward consolidation. Lower savings rank but no complications.',
  },
  // ── copp-0004: PA multi-stop — metro, sequencing consideration ──────────
  {
    id: 'copp-0004', tenantId: TENANT, type: 'multi-stop',
    loadIds: ['load-00022', 'load-00023', 'load-00024'], lane: 'PA multi',
    estSavings: 420, score: 68, status: 'open',
    reason: '3 stops within Philadelphia metro — single truck multi-stop saves ~$420.',
    identifiedAt: iso(0, 12),
    challenges: [
      dim('service_risk', 'Philadelphia metro drayage carriers experienced; capacity available for multi-stop.', 'none'),
      dim('appointment_window_conflict', 'Tight urban delivery windows require precise sequencing — confirm all 3 receiver windows before dispatch.', 'low'),
      dim('accessorial_blowback', 'Multi-stop billing adds $35–50 in stop charges — net savings ~$370. Still positive.', 'low'),
      dim('weight_class_escalation', 'Combined weight within single FTL limits — no class change.', 'none'),
      dim('customer_service_conflict', '3 different receivers in Philly metro — confirm delivery sequence matches all SLAs before committing.', 'low'),
    ],
    overallRisk: 'low', proceedRecommendation: true,
    confidence: 0.68,
    confidenceFactors: ['metro_consolidation', 'stop_sequence_verified', 'net_savings_positive_after_stop_charges'],
    narrative: '3-stop consolidation in Philadelphia metro — $420 gross savings, net ~$370 after stop charges. Urban delivery windows require sequence planning, but the lane is well-established and all 3 stops are within 12 miles.',
    challengeSummary: 'Low risk. Confirm delivery sequence with all 3 Philadelphia receivers and account for ~$50 stop charges in net savings calculation.',
  },
];

// ── TMS-4 Emissions Factors + Reports ─────────────────────────────────
export const SEED_EMISSIONS_FACTORS = [
  { id: 'ef-ftl-diesel', tenantId: TENANT, mode: 'FTL', fuelType: 'diesel', gramsPerTonMile: 161, source: 'EPA SmartWay avg 2025' },
  { id: 'ef-ltl-diesel', tenantId: TENANT, mode: 'LTL', fuelType: 'diesel', gramsPerTonMile: 185, source: 'EPA SmartWay avg 2025' },
  { id: 'ef-reef-diesel', tenantId: TENANT, mode: 'Reefer', fuelType: 'diesel', gramsPerTonMile: 195, source: 'EPA SmartWay refrigerated' },
  { id: 'ef-im-diesel', tenantId: TENANT, mode: 'Intermodal', fuelType: 'diesel', gramsPerTonMile: 88, source: 'AAR intermodal efficiency' },
  { id: 'ef-ocean', tenantId: TENANT, mode: 'Ocean', fuelType: 'diesel', gramsPerTonMile: 11, source: 'IMO 2023' },
  { id: 'ef-air', tenantId: TENANT, mode: 'Air', fuelType: 'jet', gramsPerTonMile: 500, source: 'IATA 2024' },
];

export const SEED_EMISSIONS_REPORTS = [
  { id: 'er-2026-q1', tenantId: TENANT, period: '2026-Q1', totalKg: 182000, byMode: { FTL: 120000, LTL: 36000, Reefer: 22000, Intermodal: 4000 }, createdAt: iso(30) },
];

// ── TMS-5 Scenarios ───────────────────────────────────────────────────
export const SEED_SCENARIOS = [
  {
    id: 'scn-0001', tenantId: TENANT, name: 'Drop bottom-tier carrier',
    status: 'simulated', baselinePeriodDays: 90, createdBy: 'admin',
    hypotheses: [{ type: 'remove-carrier', carrierId: 'carrier-rr' }],
    outputs: {
      deltas: { totalCost: 14200, otpPoints: -1.8, marginPct: -0.8, emissionsKg: 520 },
    },
    aegisNarrative: 'Removing Redwood Regional adds $14.2k in substitute carrier cost but lifts on-time risk — Saia absorbs most freight at +8% rate and –1.8 pts OTP.',
    createdAt: iso(3),
  },
  {
    id: 'scn-0002', tenantId: TENANT, name: 'Shift 20% FTL to Intermodal',
    status: 'simulated', baselinePeriodDays: 90, createdBy: 'admin',
    hypotheses: [{ type: 'change-mode-mix', from: 'FTL', to: 'Intermodal' }],
    outputs: {
      deltas: { totalCost: -28400, otpPoints: -0.6, marginPct: 2.3, emissionsKg: -14800 },
    },
    aegisNarrative: 'Shifting 20% of FTL to Intermodal saves $28.4k and cuts emissions by 14.8 tonnes CO2e. On-time degrades 0.6 pts — acceptable for non-time-critical lanes.',
    createdAt: iso(5),
  },
];

// ── TMS-6 Multi-Modal + Cross-Border ──────────────────────────────────
export const SEED_CUSTOMS_DOCUMENTS = [
  { id: 'cd-0001', tenantId: TENANT, loadId: 'load-intl-001', type: 'Commercial Invoice', status: 'uploaded', uploadedAt: iso(3), url: 'seed://commercial-invoice-1.pdf', uploadedBy: 'import-clerk' },
  { id: 'cd-0002', tenantId: TENANT, loadId: 'load-intl-001', type: 'USMCA Cert of Origin', status: 'uploaded', uploadedAt: iso(3), url: 'seed://usmca-cert.pdf', uploadedBy: 'import-clerk' },
  { id: 'cd-0003', tenantId: TENANT, loadId: 'load-intl-001', type: 'ACE eManifest', status: 'pending', uploadedBy: 'import-clerk' },
];

export const SEED_MULTIMODAL_LOADS = [
  {
    id: 'load-mmod-001', tenantId: TENANT, loadNumber: 'MM-2026-001',
    multiModal: true, status: 'in_transit',
    customerId: 'cust-watchmen', customerName: 'Watchmen Logistics',
    origin: { raw: 'Chicago, IL', state: 'IL' },
    destination: { raw: 'Los Angeles, CA', state: 'CA' },
    pickupDate: iso(4), deliveryDate: iso(-2),
    distanceMiles: 2015,
    weight: 42000, transportMode: 'Intermodal',
    financials: { customerRate: 4200, carrierRate: 3280 },
    segments: [
      { segmentIndex: 0, mode: 'Drayage', carrierId: 'carrier-od', origin: 'Chicago, IL', destination: 'Cicero BNSF Ramp', pickupDate: iso(4), deliveryDate: iso(3, 18), cost: 420, status: 'completed', completedAt: iso(3) },
      { segmentIndex: 1, mode: 'Rail', carrierId: 'BNSF', origin: 'Cicero BNSF Ramp', destination: 'San Bernardino BNSF Ramp', pickupDate: iso(3), deliveryDate: iso(0), cost: 2140, status: 'active' },
      { segmentIndex: 2, mode: 'Drayage', carrierId: 'carrier-saia', origin: 'San Bernardino BNSF Ramp', destination: 'Los Angeles, CA', pickupDate: iso(0), deliveryDate: iso(-2), cost: 720, status: 'pending' },
    ],
  },
  {
    id: 'load-intl-001', tenantId: TENANT, loadNumber: 'CB-2026-001',
    multiModal: true, status: 'customs_hold',
    customerId: 'cust-riverside', customerName: 'Riverside Natural Foods',
    origin: { raw: 'Monterrey, MX', state: 'NL', country: 'MX' },
    destination: { raw: 'Laredo, TX', state: 'TX', country: 'US' },
    pickupDate: iso(3), deliveryDate: iso(-1),
    distanceMiles: 157,
    weight: 38000, transportMode: 'FTL',
    financials: { customerRate: 1650, carrierRate: 1280 },
    crossBorder: { from: 'MX', to: 'US', documents: ['Commercial Invoice', 'USMCA Cert of Origin', 'ACE eManifest'], customsStatus: 'pending_manifest' },
    segments: [
      { segmentIndex: 0, mode: 'FTL', carrierId: 'carrier-mx', origin: 'Monterrey, MX', destination: 'Nuevo Laredo Border', pickupDate: iso(3), deliveryDate: iso(2), cost: 640, status: 'completed' },
      { segmentIndex: 1, mode: 'FTL', carrierId: 'carrier-od', origin: 'Laredo TX Border', destination: 'Laredo, TX', pickupDate: iso(1), deliveryDate: iso(-1), cost: 640, status: 'active' },
    ],
  },
];

export const TMS_OPS_SEED_SUMMARY = {
  rate_confirmations: SEED_RATE_CONFIRMATIONS.length,
  carrier_invoices: SEED_CARRIER_INVOICES.length,
  disputes: SEED_DISPUTES.length,
  consolidation_opportunities: SEED_CONSOLIDATION_OPPORTUNITIES.length,
  emissions_factors: SEED_EMISSIONS_FACTORS.length,
  emissions_reports: SEED_EMISSIONS_REPORTS.length,
  scenarios: SEED_SCENARIOS.length,
  customs_documents: SEED_CUSTOMS_DOCUMENTS.length,
  multimodal_loads: SEED_MULTIMODAL_LOADS.length,
};
